import { useEffect, useState } from "react";
import type {
  PlatformApiClient,
  ProspectDemoRequestDetail,
} from "../api";
import { PlatformApiError } from "../api";
import {
  Page,
  PageHeader,
  StatusState,
} from "../adaptive/primitives";

function statusLabel(status: ProspectDemoRequestDetail["request"]["status"]): string {
  if (status === "requested") return "REQUESTED";
  if (status === "accepted") return "ACCEPTED";
  if (status === "in_progress") return "IN PROGRESS";
  if (status === "ready") return "READY";
  return "CANCELLED";
}

export function ProspectDemoWorkspace({
  api,
  requestId,
  navigate,
}: {
  api: PlatformApiClient;
  requestId: string;
  navigate: (path: string) => void;
}) {
  const [detail, setDetail] =
    useState<ProspectDemoRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.prospectDemoRequest(requestId)
      .then((loaded) => {
        if (!active) return;
        setDetail(loaded);
        setError(null);
      })
      .catch((cause) => {
        if (!active) return;
        setError(
          cause instanceof PlatformApiError
            ? cause.message
            : "No se pudo cargar el Prospect Demo.",
        );
      });
    return () => {
      active = false;
    };
  }, [api, requestId]);

  if (error) {
    return <Page>
      <StatusState
        eyebrow="Prospect Demo"
        title="No se pudo abrir"
        message={error}
        action={
          <button onClick={() => navigate("/demos")}>
            Volver a Demos
          </button>
        }
      />
    </Page>;
  }

  if (!detail) {
    return <Page>
      <StatusState
        eyebrow="Prospect Demo"
        title="Abriendo demo…"
        message="Cargando identidad, execution path y artefacto materializado."
      />
    </Page>;
  }

  const request = detail.request;
  const execution = detail.execution.latest;
  const targetUrl =
    execution?.outcome === "materialized"
      ? execution.targetUrl
      : null;

  return <Page>
    <PageHeader
      eyebrow="Prospect Demo"
      title={request.businessName}
      description={
        `${request.request.market.industryLabel ?? request.industryKey} · ${request.request.market.nicheLabel ?? request.nicheKey} · ${request.request.recommendedSolution.label ?? request.solutionKey}`
      }
      actions={
        <div className="button-row">
          <button onClick={() => navigate("/demos")}>
            Volver al registry
          </button>
          {targetUrl && targetUrl.startsWith("/") &&
            <button
              className="primary-button"
              onClick={() => navigate(targetUrl)}
            >
              Abrir experiencia
            </button>}
          {targetUrl && !targetUrl.startsWith("/") &&
            <a
              className="primary-button"
              href={targetUrl}
              target="_blank"
              rel="noreferrer"
            >
              Abrir experiencia ↗
            </a>}
        </div>
      }
    />

    <section className="details-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Estado</p>
          <h2>{statusLabel(request.status)}</h2>
          <p>
            Este wrapper representa una Demo real y trazable para este
            prospecto. No convierte un runtime faltante en disponible.
          </p>
        </div>
      </div>
      <dl>
        <div>
          <dt>Request</dt>
          <dd><code>{request.id}</code></dd>
        </div>
        <div>
          <dt>Creation path</dt>
          <dd>{request.creationPath}</dd>
        </div>
        <div>
          <dt>Solution</dt>
          <dd><code>{request.solutionKey}</code></dd>
        </div>
        <div>
          <dt>Template</dt>
          <dd>{request.templateKey ? <code>{request.templateKey}</code> : "—"}</dd>
        </div>
        <div>
          <dt>Reference Demo</dt>
          <dd>{request.referenceDemoId ? <code>{request.referenceDemoId}</code> : "—"}</dd>
        </div>
        <div>
          <dt>Demo ID</dt>
          <dd>{request.demoId ? <code>{request.demoId}</code> : "—"}</dd>
        </div>
      </dl>
    </section>

    <section className="details-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Execution adapter</p>
          <h2>
            {execution
              ? `${execution.adapter} · ${execution.outcome}`
              : "Todavía no ejecutado"}
          </h2>
        </div>
      </div>
      {!execution
        ? <p className="muted">
            Un operador todavía no materializó este Prospect Demo.
          </p>
        : <>
          <p>{execution.detail}</p>
          {execution.reasonCode &&
            <p className="inline-alert">
              <strong>Bloqueo:</strong> {execution.reasonCode}
            </p>}
          <dl>
            <div>
              <dt>Source artifact</dt>
              <dd>
                {execution.sourceArtifactKind ?? "—"}
                {execution.sourceArtifactId
                  ? <> · <code>{execution.sourceArtifactId}</code></>
                  : null}
              </dd>
            </div>
            <div>
              <dt>Target</dt>
              <dd>
                {execution.targetUrl
                  ? <code>{execution.targetUrl}</code>
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Attempts</dt>
              <dd>{detail.execution.attempts.length}</dd>
            </div>
          </dl>
        </>}
    </section>

    <section className="details-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Lifecycle</p>
          <h2>Audit trail</h2>
        </div>
      </div>
      <ol>
        {detail.events.map((event) =>
          <li key={event.id}>
            <strong>{event.eventType}</strong>
            {" · "}
            {event.toStatus}
            {" · "}
            <small>{event.createdAt}</small>
          </li>
        )}
      </ol>
    </section>
  </Page>;
}
